import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { VideoItem } from "../../components/video/VideoItem";
import { Add } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import { AddVideoDialog } from "../../components/video/AddVideoDialog";
import { useSnackbar } from "notistack";

const VideoPage = () => {
  const { token } = useAuthContext();
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [videos, setVideos] = useState([]); // State to store exercises fetched from backend
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [filteredVideos, setFilteredVideos] = useState([]); // State for filtered exercises
  const { darkMode } = useContext(ThemeContext);
  const [openAddDialog, setOpenAddDialog] = useState(false); // New state for confirmation dialog
  const { enqueueSnackbar } = useSnackbar();

  const updateVideo = (updatedVideo) => {
    setVideos((prevVideos) => {
      let updatedVideos;

      if (updatedVideo.deleted) {
        // Remove the deleted exercise from the state
        updatedVideos = prevVideos.filter(
          (video) => video.id !== updatedVideo.id
        );
      } else if (!prevVideos.some((video) => video.id === updatedVideo.id)) {
        // Add the new exercise if it doesn't already exist
        updatedVideos = [...prevVideos, updatedVideo];
      } else {
        // Update the existing exercise
        updatedVideos = prevVideos.map((video) =>
          video.id === updatedVideo.id ? updatedVideo : video
        );
      }

      // Sort exercises alphabetically by name
      return updatedVideos.sort((a, b) =>
        a.displayname.localeCompare(b.displayname, undefined, {
          sensitivity: "base",
        })
      );
    });
  };

  useEffect(() => {
    // Fetch exercises from the backend
    if (!token) return;
    const fetchVideos = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/video`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setVideos(response.data);
        setFilteredVideos(response.data); // Initialize filtered exercises
      } catch (err) {
        console.error("Error fetching videos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [token]);

  useEffect(() => {
    // Filter exercises based on the search query
    setFilteredVideos(
      videos.filter((video) =>
        video.displayname.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, videos]);

  const handleAddVideo = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/video`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      updateVideo(response.data); // Update the parent state
      enqueueSnackbar("Successfully Added!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to add video!", { variant: "error" });
    }
  };

  const handleAddClicked = () => {
    setOpenAddDialog(true);
  };

  const handleCancelAdd = () => {
    setOpenAddDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          pt: "60px",
          pl: 5,
          pr: 10,
        }}
      >
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
        {!loading && (
          <>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "28px",
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                fontWeight: 600,
                textAlign: "left",
                mb: 5,
              }}
            >
              Global Video Management
            </Typography>
            <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
              <TextField
                variant="filled"
                placeholder="Search videos"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <SearchIcon sx={{ mt: -2, ml: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                startIcon={<Add />}
                variant="contained"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 500,
                  px: 3,
                  py: 2,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleAddClicked}
              >
                Add Video
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={4}
              rowGap={4}
            >
              {filteredVideos.map((video, index) => (
                <VideoItem
                  key={index}
                  {...video}
                  onVideoUpdate={updateVideo}
                  allVideos={videos}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
      <AddVideoDialog
        open={openAddDialog}
        onClose={handleCancelAdd}
        onAdd={handleAddVideo}
        allVideos={videos}
      />
    </>
  );
};

export default VideoPage;
